import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CPlaceholder,
  CRow,
} from '@coreui/react-pro'
import { CChart } from '@coreui/react-chartjs'
import PlatformAnalyticsService from 'src/service/PlatformAnalyticsService'
import { useChartTheme, baseChartOptions, axis } from '../AnalyticsV2/chartTheme'
import { ErrorState, computeDelta, formatDateLabel, formatNumber } from '../AnalyticsV2/common'
import RankedBarList from '../AnalyticsV2/RankedBarList'
import {
  deviceLabel,
  errorMessage,
  formatDateTime,
  gameTypeLabel,
  timeAgo,
} from './platformHelpers'

const KPIS = [
  { key: 'sessions', label: 'Sessions' },
  { key: 'screen_views', label: 'Screens viewed' },
  { key: 'actions', label: 'Taps & shares' },
  { key: 'games', label: 'Games used' },
]

const Session = ({ s, onOpenGame }) => {
  const [open, setOpen] = useState(false)
  const path = s.path || []
  const shown = open ? path : path.slice(0, 8)

  return (
    <li className="pa-session">
      <div className="d-flex flex-wrap align-items-baseline gap-2 mb-1">
        <strong>{formatDateTime(s.started_at)}</strong>
        <span className="small text-medium-emphasis">
          {s.minutes > 0 ? `${s.minutes} min` : '< 1 min'} · {deviceLabel(s.device_type)}
          {s.app_version ? ` v${s.app_version}` : ''}
        </span>
      </div>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 mb-2 text-start"
        onClick={() => onOpenGame({ id: s.game_id, game_name: s.game_name })}
      >
        {s.game_name}
      </button>
      {path.length > 0 ? (
        <div className="pa-path">
          {shown.map((step, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <span className="pa-path-arrow" aria-hidden="true">
                  ›
                </span>
              )}
              <span
                className={`pa-path-step${step.type === 'action' ? ' pa-path-step--action' : ''}`}
                title={`${step.type === 'action' ? 'Action' : 'Screen'} · ${formatDateTime(
                  step.at,
                )}`}
              >
                {step.label}
              </span>
            </React.Fragment>
          ))}
          {path.length > 8 && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-1"
              onClick={() => setOpen(!open)}
            >
              {open ? 'Show less' : `+${path.length - 8} more`}
            </button>
          )}
        </div>
      ) : (
        <div className="small text-medium-emphasis">No screens recorded.</div>
      )}
    </li>
  )
}

const UserDetailOffcanvas = ({ user, filters, reloadToken, onClose, onOpenGame }) => {
  const theme = useChartTheme()
  const [state, setState] = useState({ data: null, loading: false, error: null })
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    if (!user) return undefined
    let cancelled = false
    setState({ data: null, loading: true, error: null })
    PlatformAnalyticsService.getUser(filters, user.id)
      .then((res) => {
        if (cancelled) return
        setState(
          res?.success
            ? { data: res.data, loading: false, error: null }
            : { data: null, loading: false, error: errorMessage(res) },
        )
      })
      .catch(
        (err) => !cancelled && setState({ data: null, loading: false, error: errorMessage(err) }),
      )
    return () => {
      cancelled = true
    }
  }, [user, filters, reloadToken, retry])

  const d = state.data
  const p = d?.profile
  const hasDaily = d?.daily?.some((r) => r.value > 0)

  return (
    <COffcanvas placement="end" visible={Boolean(user)} onHide={onClose} className="pa-offcanvas">
      <COffcanvasHeader className="border-bottom">
        <div className="min-w-0">
          <COffcanvasTitle className="text-truncate d-flex align-items-center gap-2">
            {d?.online && <span className="pa-online-dot" title="Online now" />}
            {p?.full_name || user?.full_name || 'Person'}
          </COffcanvasTitle>
          {p && (
            <div className="small text-medium-emphasis text-truncate">
              #{p.id} · {p.email || 'no email'}
              {p.phone ? ` · ${p.phone}` : ''}
            </div>
          )}
        </div>
        <CCloseButton className="text-reset ms-auto" onClick={onClose} />
      </COffcanvasHeader>

      <COffcanvasBody>
        {state.loading && (
          <CPlaceholder component="div" animation="glow">
            <CPlaceholder xs={7} className="mb-3" />
            <CPlaceholder xs={12} style={{ height: 90 }} className="mb-3" />
            <CPlaceholder xs={12} style={{ height: 220 }} />
          </CPlaceholder>
        )}
        {state.error && (
          <ErrorState message={state.error} onRetry={() => setRetry((r) => r + 1)} height={240} />
        )}

        {d && p && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <CBadge color={d.online ? 'success' : 'secondary'} shape="rounded-pill">
                {d.online ? 'Online now' : `Last seen ${timeAgo(d.last_seen) || 'never'}`}
              </CBadge>
              {d.first_seen && (
                <span className="av2-chip small" title={formatDateTime(d.first_seen)}>
                  First opened the app {timeAgo(d.first_seen)}
                </span>
              )}
              {p.created_at && (
                <span className="av2-chip small">
                  Account created {formatDateLabel(String(p.created_at).slice(0, 10), true)}
                </span>
              )}
            </div>

            <CRow className="g-2 mb-3">
              {KPIS.map((k) => {
                const delta = computeDelta(d.current[k.key], d.previous[k.key])
                return (
                  <CCol xs={6} key={k.key}>
                    <div className="pa-mini">
                      <div className="small text-medium-emphasis">{k.label}</div>
                      <div className="fs-5 fw-semibold">{formatNumber(d.current[k.key])}</div>
                      <div className={`small text-${delta.color}`}>{delta.label}</div>
                    </div>
                  </CCol>
                )
              })}
            </CRow>

            <h6 className="mb-2">Sessions per day</h6>
            {hasDaily ? (
              <div className="av2-chart pa-chart--drawer mb-4">
                <CChart
                  customTooltips={false}
                  type="bar"
                  data={{
                    labels: d.daily.map((r) => formatDateLabel(r.bucket)),
                    datasets: [
                      {
                        label: 'Sessions',
                        data: d.daily.map((r) => r.value),
                        backgroundColor: theme.series[0],
                        borderRadius: 4,
                        maxBarThickness: 18,
                      },
                    ],
                  }}
                  options={baseChartOptions(theme, {
                    plugins: { legend: { display: false } },
                    scales: {
                      x: axis(theme, { grid: { display: false }, ticks: { maxTicksLimit: 6 } }),
                      y: axis(theme, { beginAtZero: true, ticks: { precision: 0 } }),
                    },
                  })}
                />
              </div>
            ) : (
              <div className="small text-medium-emphasis mb-4">
                No app sessions in the selected period.
              </div>
            )}

            <h6 className="mb-2">Games</h6>
            {d.games.length === 0 ? (
              <div className="small text-medium-emphasis mb-4">Not in any game.</div>
            ) : (
              <ul className="pa-user-games mb-4">
                {d.games.map((g) => (
                  <li key={g.game_id}>
                    <button
                      type="button"
                      className="pa-user-game"
                      onClick={() => onOpenGame({ id: g.game_id, game_name: g.game_name })}
                    >
                      <span className="min-w-0">
                        <span className="d-block fw-semibold text-truncate">{g.game_name}</span>
                        <span className="d-block small text-medium-emphasis text-truncate">
                          {g.game_type ? gameTypeLabel(g.game_type) : 'Game'} ·{' '}
                          {g.is_member
                            ? `${g.member_active ? 'Member' : 'Inactive member'}${
                                g.joined_at ? ` since ${String(g.joined_at).slice(0, 10)}` : ''
                              }`
                            : 'Not a member'}
                        </span>
                      </span>
                      <span className="text-end flex-shrink-0">
                        <span className="d-block fw-semibold">
                          {formatNumber(g.sessions)} sessions
                        </span>
                        <span className="d-block small text-medium-emphasis">
                          {g.last_seen ? timeAgo(g.last_seen) : 'not this period'}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <h6 className="mb-2">Devices</h6>
            <ul className="pa-legend mb-4">
              {d.devices.length === 0 && (
                <li className="text-medium-emphasis">Never opened the app.</li>
              )}
              {d.devices.map((dev, i) => (
                <li key={i}>
                  <span className="me-auto">
                    <strong>{deviceLabel(dev.device_type)}</strong>
                    <span className="text-medium-emphasis">
                      {' '}
                      {[
                        dev.device_model,
                        dev.os_version && `OS ${dev.os_version}`,
                        dev.app_version && `v${dev.app_version}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="small text-medium-emphasis text-nowrap">
                    {timeAgo(dev.last_seen)}
                  </span>
                </li>
              ))}
            </ul>

            <h6 className="mb-2">Screens they visit most</h6>
            <div className="mb-4">
              <RankedBarList
                rows={d.top_screens.map((s) => ({
                  key: s.page_name,
                  label: s.page_name,
                  value: s.views,
                }))}
                color={theme.series[0]}
                valueLabel="views"
                emptyLabel="No screen views in this period."
              />
            </div>

            <h6 className="mb-1">Recent sessions</h6>
            <div className="small text-medium-emphasis mb-2">
              Latest {d.sessions.length} in this period — screens in order, actions in italics
            </div>
            {d.sessions.length === 0 ? (
              <div className="small text-medium-emphasis">No sessions in this period.</div>
            ) : (
              <ol className="pa-sessions">
                {d.sessions.map((s) => (
                  <Session key={s.session_id} s={s} onOpenGame={onOpenGame} />
                ))}
              </ol>
            )}
          </>
        )}
      </COffcanvasBody>
    </COffcanvas>
  )
}

export default UserDetailOffcanvas
